import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export function AIConcierge() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => `web_${Math.random().toString(36).slice(2)}`);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm ERRAH AI. Tell me about your surface — marble, wood, glass, a countertop — and I'll suggest the right protection." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (_) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm having trouble right now. Please try again shortly." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="concierge-open"
          className="fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-40 inline-flex items-center gap-2 bg-[#C9A45C] text-black px-5 py-3 text-sm font-semibold shadow-[0_8px_28px_rgba(212,175,55,0.35)] hover:bg-[#E4C987] transition-colors">
          <Sparkles size={16} /> ERRAH AI
        </button>
      )}
      {open && (
        <div className="fixed bottom-20 right-3 lg:bottom-6 lg:right-6 z-40 w-[92vw] max-w-sm h-[68vh] max-h-[560px] flex flex-col glass shadow-[0_8px_40px_rgba(0,0,0,0.7)]" data-testid="concierge-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <span className="w-8 h-8 flex items-center justify-center bg-[#C9A45C] text-black"><Sparkles size={16} /></span>
              <div>
                <p className="font-display text-sm font-medium">ERRAH AI</p>
                <p className="text-[10px] text-gray-500">Surface protection concierge</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="concierge-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-[#C9A45C] text-black" : "bg-white/[0.04] text-gray-200 border border-white/5"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-gray-500 flex items-center gap-2"><span className="w-3 h-3 border-2 border-[#C9A45C] border-t-transparent rounded-full animate-spin" /> ERRAH AI is thinking…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex items-center gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="concierge-input" placeholder="Ask about protection…"
              className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-[#C9A45C]" />
            <button onClick={send} data-testid="concierge-send" className="bg-[#C9A45C] text-black p-2 hover:bg-[#E4C987] transition-colors"><Send size={16} /></button>
          </div>
        </div>
      )}
    </>
  );
}
